import { toAppVideo, getThumbnailUrl, deriveTitleFromUrl } from "./videoHelper";

// Utility functions for managing favorites in localStorage

const FAVORITES_KEY = 'video_favorites';

// Internal: normalize a raw stored entry into { id, video }
const normalizeEntry = (entry) => {
  if (!entry) return null;
  if (typeof entry === 'string') {
    const id = entry;
    // Shortcut: if it looks like an encoded/external URL, try to derive
    let video = null;
    try {
      if (id.indexOf('%3A') !== -1 || id.startsWith('http')) {
        const maybeUrl = decodeURIComponent(id);
        video = toAppVideo({ url: maybeUrl });
      }
    } catch (e) { /* ignore */ }

    // If still missing and id looks like a raw service id (youtube-ish), synthesize
    const looksLikeId = typeof id === 'string' && /^[A-Za-z0-9_-]{8,}$/.test(id);
    if (!video && looksLikeId) {
      // Treat as YouTube id by default (best-effort)
      video = { videoId: id, originalUrl: `https://www.youtube.com/watch?v=${id}`, platform: 'youtube' };
    }

    return { id, video };
  }
  if (typeof entry === 'object') {
    if (entry.id) {
      const vid = entry.video ? (toAppVideo(entry.video) || entry.video) : null;
      return { id: entry.id, video: vid };
    }
    if (entry.videoId) {
      const vid = toAppVideo(entry) || entry;
      return { id: entry.videoId, video: vid };
    }
    // fallback to using originalUrl or other fields
    const id = entry.originalUrl || entry.url || entry.videoId || null;
    const video = toAppVideo(entry) || entry;
    return { id, video };
  }
  return null;
};

// Ensure minimal safe fields on stored video objects
const ensureSafeVideo = (v, idFallback) => {
  if (!v) return null;
  const vid = { ...v };

  vid.videoId = vid.videoId || idFallback || vid.id || null;
  vid.originalUrl = vid.originalUrl || vid.url || vid.originalUrl || null;

  // If we have a raw-looking id but no originalUrl, assume YouTube watch URL (best-effort)
  const looksLikeId = typeof vid.videoId === 'string' && /^[A-Za-z0-9_-]{8,}$/.test(vid.videoId);
  if (!vid.originalUrl && looksLikeId) {
    vid.originalUrl = `https://www.youtube.com/watch?v=${vid.videoId}`;
    if (!vid.platform) vid.platform = 'youtube';
  }

  // Derive title: prefer existing, else try deriving from URL or id
  const derivedTitle = (vid.originalUrl ? (deriveTitleFromUrl(vid.originalUrl) || null) : null) || (vid.videoId ? `YouTube video ${vid.videoId}` : null);
  vid.title = (vid.title && vid.title !== '(No title)') ? vid.title : (derivedTitle || '(No title)');

  // Thumbnails: prefer provided thumbnails, else derive from known services, else fallback to default
  let thumbUrl = null;
  if (Array.isArray(vid.thumbnails) && vid.thumbnails.length > 0 && vid.thumbnails[0].url) {
    thumbUrl = vid.thumbnails[0].url;
  } else {
    thumbUrl = getThumbnailUrl(vid.originalUrl) || (vid.videoId ? `https://i.ytimg.com/vi/${vid.videoId}/hqdefault.jpg` : null);
  }
  vid.thumbnails = [{ url: thumbUrl || '/default-thumbnail.svg' }];

  vid.stats = vid.stats || {};
  vid.stats.views = Number((vid.stats.views ?? 0)) || 0;
  vid.stats.likes = Number((vid.stats.likes ?? 0)) || 0;
  vid.publishedTimeText = vid.publishedTimeText || vid.published || '';
  return vid;
};

// Get all favorited entries (returns array of { id, video })
export const getFavorites = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (!raw) return [];
    let parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) parsed = [];

    // Normalize all entries and also migrate any legacy string-only arrays
    const normalized = parsed.map(normalizeEntry).filter(Boolean).map((e) => {
      // If video is missing but id looks like a URL, attempt to derive
      if (!e.video && e.id) {
        try {
          const maybe = toAppVideo({ url: e.id.indexOf('%3A') !== -1 ? decodeURIComponent(e.id) : e.id });
          if (maybe) e.video = maybe;
        } catch (err) { /* ignore */ }
      }
      if (e.video) e.video = ensureSafeVideo(e.video, e.id);
      return e;
    });

  // Background refresh for missing metadata (e.g., fetch YouTube oEmbed title/thumbnail)
  (async function refreshMissingMetadata(entries) {
    try {
      const needs = entries.filter(e => e && e.video && (
        !e.video.title || e.video.title === '(No title)' || /^YouTube video/i.test(e.video.title)
      ));
      if (needs.length === 0) return;

      let changed = false;

      for (const e of needs) {
        try {
          // Try YouTube oEmbed when it looks like a youtube id or platform
          const vid = e.video.videoId || e.id;
          if (!vid) continue;
          // extract a YouTube id if present
          const maybeId = (typeof vid === 'string' && /[A-Za-z0-9_-]{8,}/.test(vid)) ? vid : null;
          if (!maybeId) continue;

          const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + maybeId)}&format=json`;
          const resp = await fetch(oembedUrl);
          if (!resp.ok) continue;
          const data = await resp.json();
          if (data) {
            if (data.title && data.title !== e.video.title) {
              e.video.title = data.title;
              changed = true;
            }
            if (data.thumbnail_url) {
              e.video.thumbnails = [{ url: data.thumbnail_url }];
              changed = true;
            }
          }
        } catch (inner) {
          // ignore per-entry failures
        }
      }

      if (changed) {
        try {
          localStorage.setItem(FAVORITES_KEY, JSON.stringify(entries));
          try { window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { reason: 'metadata_refreshed' } })); } catch (e) { /* ignore */ }
        } catch (err) { /* ignore */ }
      }
    } catch (err) {
      // ignore global failures
    }
  })(normalized);


    // If migration detected (legacy strings), persist normalized form for consistency
    const legacy = parsed.some(p => typeof p === 'string');
    if (legacy) {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(normalized));
    }

    return normalized;
  } catch (error) {
    console.error('Error getting favorites:', error);
    return [];
  }
};

// Add a video (object) or an ID (string) to favorites
export const addToFavorites = (videoOrId) => {
  try {
    const favorites = getFavorites();

    let entry = null;

    if (typeof videoOrId === 'string') {
      entry = { id: videoOrId, video: null };
      // Try to normalize URL-based ids into a video payload
      try {
        const maybe = toAppVideo({ url: videoOrId.indexOf('%3A') !== -1 ? decodeURIComponent(videoOrId) : videoOrId });
        if (maybe) entry.video = ensureSafeVideo(maybe, entry.id);
      } catch (e) { /* ignore */ }
    } else if (typeof videoOrId === 'object') {
      // Prefer explicit videoId property when available
      const id = videoOrId.videoId || videoOrId.id || videoOrId.originalUrl || videoOrId.url || null;
      const normalized = toAppVideo(videoOrId) || videoOrId;
      entry = { id, video: ensureSafeVideo(normalized, id) };
    } else {
      return false;
    }

    if (!entry.id) {
      // invalid entry
      return false;
    }

    // If already present, update stored video payload (if provided)
    const existingIdx = favorites.findIndex(f => f.id === entry.id);
    if (existingIdx !== -1) {
      // replace video payload if available
      if (entry.video) favorites[existingIdx].video = entry.video;
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
      return true;
    }

    favorites.push(entry);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    try { window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { id: entry.id, reason: 'added' } })); } catch (e) { /* ignore */ }
    return true;
  } catch (error) {
    console.error('Error adding to favorites:', error);
    return false;
  }
};

// Remove a video from favorites by id
export const removeFromFavorites = (videoId) => {
  try {
    const favorites = getFavorites();
    const updatedFavorites = favorites.filter(f => f.id !== videoId);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(updatedFavorites));
    try { window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { id: videoId, reason: 'removed' } })); } catch (e) { /* ignore */ }
    return true;
  } catch (error) {
    console.error('Error removing from favorites:', error);
    return false;
  }
};

// Check if a video is favorited (by id)
export const isFavorited = (videoId) => {
  const favorites = getFavorites();
  return !!favorites.find(f => f.id === videoId);
};